import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { Column, Row } from "@/shared/ui/Blocks";
import { Icon } from "@/shared/ui/Icon";
import { Button } from "@/shared/ui/Button";
import { cn } from "@/shared/utils/shadcn";
import { IconButton } from "../IconButton";

export const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "text/plain": [".txt"],
} as const;

export interface FileDropzoneProps {
  onFileSelect: (file: File) => void;
  fileError?: string | null;
  acceptedFileTypes?: Record<string, string[]>;
  multiple?: boolean;
  variant?: "default" | "compact";
  titleHidden?: boolean;
  buttonLabel?: string;
}

const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFileSelect,
  fileError,
  acceptedFileTypes = ACCEPTED_FILE_TYPES,
  multiple = false,
  variant = "default",
  titleHidden = false,
  buttonLabel,
}) => {
  const { t } = useTranslation();

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      if (rejectedFiles.length > 0) {
        return;
      }

      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
    },
    [onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple,
    accept: acceptedFileTypes,
  });

  if (variant === "compact") {
    return (
      <div
        className={cn(
          "flex flex-1 items-center justify-between w-full h-[48px] rounded-lg border border-gray-100 bg-grey-100 py-2 px-4",
          isDragActive && "border-blue-500 bg-grey-200"
        )}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <Icon icon="Upload" size="sm" color="darkGrey" />
        {isDragActive && (
          <p className="text-gray-600">{t("common.drop_files_here")}</p>
        )}
        {!isDragActive && (
          <>
            <p className="text-gray-600">{t("common.drag_drop_or_click")}</p>
            <Button
              variant="outline"
              color="blue"
              className=" text-blue-500"
              size="sm"
            >
              {buttonLabel || t("common.select_files")}
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <Column className="flex-1 h-full gap-2l">
      <div
        {...getRootProps()}
        className={`flex relative flex-1 flex-col items-center justify-center h-full border-2 border-dashed bg-grey-100 rounded-lg p-8 text-center cursor-pointer transition-colors
          ${
            isDragActive
              ? "border-blue-500 bg-grey-200"
              : "border-gray-300 hover:border-gray-400"
          }`}
      >
        <input {...getInputProps()} />

        <Icon
          icon="Upload"
          size="lg"
          color="white"
          className={cn("absolute self-center h-full w-full p-8")}
        />
        <div className="absolute self-center p-8">
          {isDragActive && (
            <p className="text-gray-600">{t("common.drop_files_here")}</p>
          )}
          {!isDragActive && (
            <>
              {!titleHidden && (
                <p className="text-gray-600">
                  {t("common.drag_drop_or_click")}
                </p>
              )}
              <IconButton
                variant="outline"
                color="blue"
                className="mt-4 text-blue-500"
                size="md"
                icon="Upload"
                label={buttonLabel || t("common.select_files")}
              />
            </>
          )}

          <p className="text-sm text-gray-500 mt-4">
            <b>{t("common.supported_files")}:</b> <br /> PDF, DOC, DOCX, TXT
          </p>
        </div>
      </div>
      {fileError && (
        <p className="text-sm text-red-500 text-center">{fileError}</p>
      )}
    </Column>
  );
};

export default FileDropzone;
